import api, { ResponsePayloadWithData } from '.';

interface SchoolInGroup {
  id: string;
  name: string;
  cities?: string;
  isSmartschool: boolean;
}

type GroupStatus = 'unpublished' | 'published' | 'closed';

export interface Group {
  id: string;
  name: string;
  school: SchoolInGroup;
  subjects: string[];
  schoolYear: string;
  totalAccepted: number;
  totalPending: number;
  totalDeclined: number;
  invitationId: string;
  smartschoolGroupId?: string;
  status?: GroupStatus;
  code?: string;
  startDate?: string;
  endDate?: string;
}

export interface School {
  id: string;
  name: string;
  cities?: string;
  isSmartschool: boolean;
}

// Simplified version of Group for /current/groups
export interface CurrentGroup {
  id: string;
  name: string;
  school: School;
  schoolYear: string;
  isSmartschoolSynced: boolean;
  acceptedMemberCount: number;
}

export interface CurrentOrPreviousGroup extends CurrentGroup {}

/**
 * @returns An alphabetically (by name) sorted array of groups
 */
export async function fetchCurrentAndPreviousGroups(): Promise<CurrentOrPreviousGroup[]> {
  const {
    data: { data },
  } = await api.get<ResponsePayloadWithData<CurrentOrPreviousGroup[]>>(
    '/studio/user/school-years/current/previous/groups',
    { params: { studioGroup: process.env.REACT_APP_P_STUDIO_GROUP } },
  );

  return data;
}

export interface Member {
  id: string;
  primaryEmail?: string;
  status: 'accepted' | 'pending' | 'declined';
  firstName?: string;
  lastName?: string;
  password?: string;
  username?: string;
}

interface SchoolYear {
  startYear: number;
  endYear: number;
}

export async function fetchMembers(schoolYear: SchoolYear | string, groupId: string): Promise<Member[]> {
  const schoolYearString =
    typeof schoolYear === 'string' ? schoolYear : `${schoolYear.startYear}-${schoolYear.endYear}`;
  const {
    data: { data },
  } = await api.get<ResponsePayloadWithData<Member[]>>(
    `studio/user/school-years/${schoolYearString}/groups/${groupId}/members`,
  );

  return data;
}

export async function fetchGroups(schoolYear: SchoolYear): Promise<Group[]> {
  const {
    data: { data },
  } = await api.get<ResponsePayloadWithData<Group[]>>(
    `studio/user/school-years/${schoolYear.startYear}-${schoolYear.endYear}/groups`,
    {
      params: { studioGroup: process.env.REACT_APP_P_STUDIO_GROUP },
    },
  );

  return data;
}
